import React, { useMemo, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { InstanceComment } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';

const INSTANCE_COMMENTS_QUERY_KEY = 'ACTION_LIBRARY_INSTANCE_COMMENTS';

type InstanceCommentsDialogProps = {
  open: boolean;
  onClose: () => void;
  instanceId: number;
};

type SortOrder = 'desc' | 'asc';

const InstanceCommentsDialog: React.FC<InstanceCommentsDialogProps> = ({ open, onClose, instanceId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [newCommentText, setNewCommentText] = useState('');
  const [dateSortOrder, setDateSortOrder] = useState<SortOrder>('desc');

  const { data: commentsData, isLoading } = useQuery(
    [INSTANCE_COMMENTS_QUERY_KEY, instanceId],
    () => actionLibraryInstancesApi.getInstanceComments(instanceId).then(res => res.data),
    { enabled: open && !!instanceId },
  );

  const createCommentMutation = useMutation((text: string) => actionLibraryInstancesApi.createInstanceComment(instanceId, { text }), {
    onSuccess: () => {
      queryClient.invalidateQueries([INSTANCE_COMMENTS_QUERY_KEY, instanceId]);
      setNewCommentText('');
    },
  });

  const rawComments = commentsData?.results ?? [];
  const sortedComments = useMemo(
    () =>
      [...rawComments].sort((a, b) => {
        const timeA = new Date(a.createdAt).getTime();
        const timeB = new Date(b.createdAt).getTime();
        return dateSortOrder === 'desc' ? timeB - timeA : timeA - timeB;
      }),
    [rawComments, dateSortOrder],
  );

  const handleDateSortClick = () => setDateSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'));

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'yyyy-MM-dd HH:mm');
    } catch {
      return dateStr;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = newCommentText.trim();
    if (!trimmed || createCommentMutation.isLoading) return;
    createCommentMutation.mutate(trimmed);
  };

  const renderCommentsContent = () => {
    if (isLoading) {
      return (
        <Typography color='textSecondary' variant='body2'>
          {t(general_messages.loading)}
        </Typography>
      );
    }
    if (sortedComments.length === 0) {
      return (
        <Typography color='textSecondary' variant='body2'>
          {t(action_library_messages.instance_actions.instance_comments_empty)}
        </Typography>
      );
    }
    return (
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sortDirection={dateSortOrder}>
              <TableSortLabel active direction={dateSortOrder} onClick={handleDateSortClick}>
                {t(action_library_messages.instance.detail.action_log_date)}
              </TableSortLabel>
            </TableCell>
            <TableCell>{t(action_library_messages.instance.detail.action_log_user)}</TableCell>
            <TableCell>{t(action_library_messages.instance_actions.comments_column_comment)}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedComments.map((c: InstanceComment) => (
            <TableRow key={c.id}>
              <TableCell>{formatDate(c.createdAt)}</TableCell>
              <TableCell>{c.userFullName || c.userEmail || '—'}</TableCell>
              <TableCell>{c.text}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Dialog fullWidth maxWidth='sm' onClose={onClose} open={open}>
      <DialogTitle>{t(action_library_messages.instance_actions.instance_comments_dialog_title)}</DialogTitle>
      <DialogContent dividers>
        <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 16 }}>{renderCommentsContent()}</div>
        <form onSubmit={handleSubmit}>
          <TextField
            disabled={createCommentMutation.isLoading}
            fullWidth
            label={t(action_library_messages.instance_actions.instance_comments_add_placeholder)}
            multiline
            onChange={e => setNewCommentText(e.target.value)}
            placeholder={t(action_library_messages.instance_actions.instance_comments_add_placeholder)}
            rows={2}
            value={newCommentText}
            variant='outlined'
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(general_messages.close)}</Button>
        <Button
          color='primary'
          disabled={!newCommentText.trim() || createCommentMutation.isLoading}
          onClick={handleSubmit}
          variant='contained'
        >
          {t(general_messages.add)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstanceCommentsDialog;
